/* eslint-disable camelcase */
import * as React from "react"
import { ViewStyle, TextInput, Appearance } from "react-native"
import { observer } from "mobx-react-lite"
import { Formik } from "formik"
import _ from "lodash"
import moment from 'moment'

import { color } from "../../../theme"
import { Text } from "../../text/text"
import { translate } from "../../../i18n"
import { Currency, Region } from "../../../database"
import { FormProps } from "../form"
import * as yup from "yup"
import { DateTimePickerField } from "../../field/date-time-picker-field/date-time-picker-field"
import { View } from "native-base"
import { NumeralField } from "../../field/numeral-field/numeral-field"
import { ModelField } from "../../field/model-field/model-field"
// import { TextField } from "../../field/text-field/text-field"

const TEXT_CONTAINER: ViewStyle = {
  paddingHorizontal: 16,
  marginVertical: 20
}

const ROW_INPUT: ViewStyle = {
  flexDirection: 'row',
  justifyContent: 'space-between',
}

export interface FarmPrivateShrimpPriceProps extends FormProps {
  /**
   * An optional style override useful for padding & margin.
   */
  initialValues?: {
    region_id?: string,
    date?: string
    currency_id?: string,
    size_20?: number,
    size_30?: number,
    size_40?: number,
    size_50?: number,
    size_60?: number,
    size_70?: number,
    size_80?: number,
    size_90?: number,
    size_100?: number,
    size_110?: number,
    size_120?: number,
    size_130?: number,
    size_140?: number,
    size_150?: number
  }
}

/**
 * Describe your component here
 */
export const FarmPrivateShrimpPriceForm = observer(function SecondPriceForm(props: FarmPrivateShrimpPriceProps) {
  const {
    initialValues = {},
    onSubmit,
    colorScheme = Appearance.getColorScheme(),
    innerRef,
    ...rest
  } = props

  const input = {
    size_100: React.useRef<TextInput>(),
    size_90: React.useRef<TextInput>(),
    size_80: React.useRef<TextInput>(),
    size_70: React.useRef<TextInput>(),
    size_60: React.useRef<TextInput>(),
    size_50: React.useRef<TextInput>(),
    size_40: React.useRef<TextInput>(),
    size_30: React.useRef<TextInput>(),
    size_20: React.useRef<TextInput>(),
    size_110: React.useRef<TextInput>(),
    size_120: React.useRef<TextInput>(),
    size_130: React.useRef<TextInput>(),
    size_140: React.useRef<TextInput>(),
    size_150: React.useRef<TextInput>()
  }

  const nextInput = {
    size_100: () => input.size_90.current && input.size_90.current.focus(),
    size_90: () => input.size_80.current && input.size_80.current.focus(),
    size_80: () => input.size_70.current && input.size_70.current.focus(),
    size_70: () => input.size_60.current && input.size_60.current.focus(),
    size_60: () => input.size_50.current && input.size_50.current.focus(),
    size_50: () => input.size_40.current && input.size_40.current.focus(),
    size_40: () => input.size_30.current && input.size_30.current.focus(),
    size_30: () => input.size_20.current && input.size_20.current.focus(),
    size_20: () => input.size_110.current && input.size_110.current.focus(),
    size_110: () => input.size_120.current && input.size_120.current.focus(),
    size_120: () => input.size_130.current && input.size_130.current.focus(),
    size_130: () => input.size_140.current && input.size_140.current.focus(),
    size_140: () => input.size_150.current && input.size_150.current.focus(),
  }

  const validationSchema = yup.object({
    date: yup.mixed().nullable().required().label(translate("shrimpPriceScreen.date")),
    currency_id: yup.string().required().label(translate("shrimpPriceScreen.currencyId")),
    size_100: yup.number().nullable().required().label(translate("shrimpPriceScreen.size100"))
      .moreThan(0, translate("cantBeZero")),
  })

  const rowInput = (key1, key2) => (
    <View style={ROW_INPUT}>
      {key1 && <NumeralField
        name={key1}
        txPrefix="shrimpPriceScreen"
        colorScheme={colorScheme}
        input={input}
        nextInput={nextInput}
        formControlProps={{ flex: 1}}
      /> || <View flex={1} paddingX="16px" />}
      {key2 && <NumeralField
        name={key2}
        txPrefix="shrimpPriceScreen"
        colorScheme={colorScheme}
        input={input}
        nextInput={nextInput}
        formControlProps={{ flex: 1, isRequired: key2 === 'size_100'  }}
      /> || <View flex={1} paddingX="16px" />}
    </View>
  )

  return (
    <Formik
      enableReinitialize
      initialValues={{
        date: moment().format('YYYY-MM-DD'),
        currency_id: "IDR",
        size_20: null,
        size_30: null,
        size_40: null,
        size_50: null,
        size_60: null,
        size_70: null,
        size_80: null,
        size_90: null,
        size_100: null,
        size_110: null,
        size_120: null,
        size_130: null,
        size_140: null,
        size_150: null,
        remark: null,
        disclaimer: null,
        private: true,
        ...initialValues
      }}
      onSubmit={onSubmit}
      innerRef={innerRef}
      validationSchema={validationSchema}
      {...rest}
    >
      {({ values, initialValues}) => {
        return(
          <>
            <Text tx="farmShrimpPriceForm.createPrivateTitle" preset="title4" color={color.palette.gray800} marginX="16px" marginY="8px" />
            <Text tx="farmShrimpPriceForm.createPrivateSubtitle" preset="body3" color={color.palette.gray700} marginX="16px" />
            {/* <TextField */}
            {/*   name="location" */}
            {/*   txPrefix="farmShrimpPriceForm" */}
            {/*   colorScheme={colorScheme} */}
            {/*   input={input} */}
            {/*   nextInput={nextInput} */}
            {/*   formControlProps={{ isDisabled: true }} */}
            {/* /> */}
            <ModelField
              name="region_id"
              txPrefix="farmForm"
              colorScheme={colorScheme}
              formControlProps={{
                labelTx: "farmShrimpPriceForm.location",
                isRequired: true
              }}
              modelPickerProps={{
                Model: Region,
                dataLabel: "full_name",
                titleTx: "farmForm.regionIdPlaceholder",
                fetchData: (model, search, params = {}) => {
                  return (
                    model.fetch({
                      search,
                      sort: 'full_name',
                      ...params
                    }).then((models) => {
                      return model.fetch({ id__eq: '99' }).then((ms) => [...models, ...ms])
                    })
                  )
                },
                isDisabled: true,
                InputRightElement: <></>
              }}
            />
            <DateTimePickerField
              name="date"
              txPrefix="shrimpPriceScreen"
              colorScheme={colorScheme}
              formControlProps={{ isRequired: true  }}
            />
            <ModelField
              name="currency_id"
              txPrefix="shrimpPriceScreen"
              colorScheme={colorScheme}
              // formControlProps={{ isRequired: true }}
              modelPickerProps={{
                Model: Currency,
                fetchData: (model, search) => (
                  model.fetch({
                    search,
                    sort: 'name'
                  }).then((models) => (
                    _.sortBy(models, (m) => {
                      const index = ['IDR', 'USD', 'VND'].findIndex((k) => k === m.id)
                      return index < 0 ? m.name : `${index}`
                    })
                  ))
                ),
                renderSelectedItemText: (item) => item && `(${item.symbol}) ${item.name}`
              }}
            />
            <View style={TEXT_CONTAINER}>
              <Text preset="title4" tx="farmShrimpPriceForm.sizeInput" color={color.palette.gray800} />
            </View>
            {rowInput('size_150', 'size_140')}
            {rowInput('size_130', 'size_120')}
            {rowInput('size_110', 'size_100')}
            {rowInput('size_90', 'size_80')}
            {rowInput('size_70', 'size_60')}
            {rowInput('size_50', 'size_40')}
            {rowInput('size_30', 'size_20')}
            <View marginBottom="50px" />
          </>
        )
      }}
    </Formik>
  )
})

FarmPrivateShrimpPriceForm.displayName = 'FarmPrivateShrimpPriceForm'
